// LLM_analysis.jsx

import React, { useState, useRef, useEffect, useImperativeHandle } from "react";
import NavButton from "./ui/NavButton";
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Grid,
  Box,
  Collapse
} from "@mui/material";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import CodeIcon from '@mui/icons-material/Code';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningIcon from '@mui/icons-material/Warning';

// import { fetchInvestmentStrategy, postInvestmentStrategy, estimateTokenCount } from "../functions/LLM_analysis_functions";
import { 
  fetchContextField,
  fetchInvestmentStrategy, 
  // postInvestmentStrategy, 
  estimateTokenCount, 
  fetchLLMContextFields,
  QuestionTypeSelector, 
  ContextsSelector,
  getPFAlertData,
  getFavoritesAlertData,
  getOpportunitiesAlertData,
  getPortfolioData
} from "../functions/LLM_analysis_functions";

import {
  systemPrompt,
  investorProfile,
  closingPrompt,
  prettyLabel,
  getPromptSection,
  buildPrompt,
  finalizePrompt,
  questionTypePrompts,
  promptTemplates
} from "../functions/LLM_analysis_prompt";
import { callLLM, checkTokenLimit } from "../functions/LLM_analysis_functions";
import ContextPreviewPane from "./LLM_ContextPreviewPane";
import SearchBoxOnly from './SearchBoxOnly';



// ✅ Define this OUTSIDE the component
const getAlertState = (
  questionType,
  pf, pfAlerts, favAlerts, oppAlerts,
  selectedPfStocks, selectedPfAlerts, selectedFavAlerts, selectedOppAlerts,
  setSelectedPfStocks, setSelectedPfAlerts, setSelectedFavAlerts, setSelectedOppAlerts
) => {
  switch (questionType) {
    case "Specific stock - My Portfolio":
    case "My_Portfolio_Items": // ✅ NEW
      return {
        alerts: pf, // uses the same redux pf
        selectedSet: selectedPfStocks, // reuse same set
        setSelectedSet: setSelectedPfStocks,
      };
    case "Specific Portfolio Alert":
      return {
        alerts: pfAlerts,
        selectedSet: selectedPfAlerts,
        setSelectedSet: setSelectedPfAlerts,
      };
    case "Specific Favorites Alert":
      return {
        alerts: favAlerts,
        selectedSet: selectedFavAlerts,
        setSelectedSet: setSelectedFavAlerts,
      };
    case "Specific Opportunity Alert":
      return {
        alerts: oppAlerts,
        selectedSet: selectedOppAlerts,
        setSelectedSet: setSelectedOppAlerts,
      };
    default:
      return { alerts: [], selectedSet: new Set(), setSelectedSet: () => {} };
  }
};

const LLM_analysis = React.forwardRef(({ disabled }, ref) => {
  const [open, setOpen] = useState(false);
  const [strategy, setStrategy] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contextSelections, setContextSelections] = useState({});
  // const [prompt, setPrompt] = useState('');
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [marketAnalysis, setMarketAnalysis] = useState('');
  const [marketAnalysisDialogOpen, setMarketAnalysisDialogOpen] = useState(false);
  const [editingContext, setEditingContext] = useState('');
  const [portfolio, setPortfolio] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [llmResponse, setLlmResponse] = useState('');
  const [llmDialogOpen, setLlmDialogOpen] = useState(false);
  const [riskDivision, setRiskDivision] = useState('');
  const [availableContexts, setAvailableContexts] = useState({});
  const [llmModel, setLlmModel] = useState('gemini'); // default
  const [fetchedStrategy, setFetchedStrategy] = useState('');
  const [previewedContext, setPreviewedContext] = useState('');
  const [tokenCount, setTokenCount] = useState(0);
  const [contextOptions, setContextOptions] = useState([]);
  const fieldValuesRef = useRef({});
  const [showContexts, setShowContexts] = useState(true);
  const [pfAlertDialogOpen, setPfAlertDialogOpen] = useState(false);
  const [pfAlerts, setPfAlerts] = useState([]);
  const [pf, setPf] = useState([]);
  const [favAlerts, setFavAlerts] = useState([]);
  const [oppAlerts, setOppAlerts] = useState([]);
  const [showTemplates, setShowTemplates] = useState(true);
  const [selectedPfAlerts, setSelectedPfAlerts] = useState(new Set());
  const [selectedPfStocks, setSelectedPfStocks] = useState(new Set());
  const [selectedFavAlerts, setSelectedFavAlerts] = useState(new Set());
  const [selectedOppAlerts, setSelectedOppAlerts] = useState(new Set());
  const [activeTemplates, setActiveTemplates] = useState(new Set());
  const [questionType, setQuestionType] = useState('Generic Advice'); // default type
  const [tokenLimitWarning, setTokenLimitWarning] = useState(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [selectedGeneralStock, setSelectedGeneralStock] = useState(null);
  const [autoPrompt, setAutoPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");



  // ✅ correct (always up-to-date)
const { alerts, selectedSet, setSelectedSet } = getAlertState(
  questionType,
  pf, pfAlerts, favAlerts, oppAlerts,
  selectedPfStocks, selectedPfAlerts, selectedFavAlerts, selectedOppAlerts,
  setSelectedPfStocks, setSelectedPfAlerts, setSelectedFavAlerts, setSelectedOppAlerts
);


// ✅ Proper recalculation on prompt or model change:
// CORRECT in useEffect:
useEffect(() => {
  const { alerts, selectedSet } = questionType === "Specific stock - My Portfolio"
    ? { alerts: [], selectedSet: selectedPfStocks }
    : getAlertState(
        questionType,
        pf, pfAlerts, favAlerts, oppAlerts,
        selectedPfStocks, selectedPfAlerts, selectedFavAlerts, selectedOppAlerts,
        setSelectedPfStocks, setSelectedPfAlerts, setSelectedFavAlerts, setSelectedOppAlerts
      );

        // ✅ EXACT DEBUG PROOF: insert this block
  // console.log("=== DEBUG PROOF ===");
  // console.log("questionType:", questionType);
  // console.log("alerts.length:", alerts.length);
  // console.log("alerts[0]:", alerts[0]);
  // console.log("selectedSet:", Array.from(selectedSet));
  // console.log("====================");

  const ids = Array.from(selectedSet);
  const rawPrompt = buildPrompt(contextSelections);

  let contextSpecificCompany = "";
  if (questionType === "Specific stock - General" && selectedGeneralStock) {
    contextSpecificCompany = `
--- CONTEXT SPECIFIC COMPANY ---
Company: ${selectedGeneralStock.company || "N/A"}
Ticker: ${selectedGeneralStock.ticker || "N/A"}
--- END OF CONTEXT SPECIFIC COMPANY ---`;
  }

  const contextText = ids.map(id => {
  const a = alerts.find(x => x.ID === id || x.id === id);
  if (!a) return "\n\n--- CONTEXT ---\nCompany: N/A\n--- END ---";
  if (questionType === "Specific stock - My Portfolio") {
    return `\n\n--- STOCK CONTEXT ---\nCompany: ${a.Company || "N/A"}\nTicker: ${a.Ticker || "N/A"}\n--- END ---`;
  } else {
    return `\n\n--- ALERT CONTEXT ---\nCompany: ${a.company || a.Company || "N/A"}\nType: ${a.alert_type || "N/A"}\nReason: ${a.pf_alert_text || a.reason || "N/A"}\n--- END ---`;
  }
}).join('');



  // ✅ INCLUDE ACTIVE TEMPLATES TOO
  let templatesPart = "";
  activeTemplates.forEach(k => {
    templatesPart += promptTemplates[k];
  });

  const assembled = `${systemPrompt}
${questionTypePrompts[questionType] || ""}
${investorProfile}
${rawPrompt}
${contextSpecificCompany}
${contextText}
${templatesPart}`;

  setAutoPrompt(assembled);

  const expanded = finalizePrompt(assembled, contextOptions, fieldValuesRef);
  setTokenCount(estimateTokenCount(expanded));
  setTokenLimitWarning(checkTokenLimit(expanded, llmModel));

}, [
  contextSelections,
  selectedPfAlerts,
  selectedFavAlerts,
  selectedOppAlerts,
  selectedPfStocks,
  selectedGeneralStock,
  questionType,
  contextOptions,
  llmModel,
  activeTemplates // ✅ ADD THIS DEP
]);


const handlePreviewTemplate = (templateKey) => {
  setPreviewedContext(`TEMPLATE::${templateKey}`);
};

const handlePreviewContext = (item) => {
  if (item === "My_Portfolio_Items") {
    setPf(getPortfolioData());
    setQuestionType("My_Portfolio_Items");
    setPreviewedContext("");
  } else if (item === "Specific Portfolio Alert") {
    setPfAlerts(getPFAlertData());
    setQuestionType("Specific Portfolio Alert");
    setPreviewedContext("");
  } else if (item === "Specific Favorites Alert") {
    setFavAlerts(getFavoritesAlertData());
    setQuestionType("Specific Favorites Alert");
    setPreviewedContext("");
  } else if (item === "Specific Opportunity Alert") {
    setOppAlerts(getOpportunitiesAlertData());
    setQuestionType("Specific Opportunity Alert");
    setPreviewedContext("");
  } else {
    // Normal backend text
    setPreviewedContext(item);
  }
};

const getPreviewContent = (context) => {
  return fieldValuesRef.current[context] || "[No data]";
};

const handleSubmitToLLM = async () => {
  const final = finalizePrompt(autoPrompt + "\n\n" + userPrompt, contextOptions, fieldValuesRef);

  // console.log("=== FINAL SUBMITTED PROMPT ===");
  // console.log(final);
  // console.log("==============================");

  const warning = checkTokenLimit(final, llmModel);
  if (warning) {
    alert(warning);
    return;
  }

  setSubmitting(true);
  setLlmDialogOpen(true);
  const result = await callLLM(final, llmModel);
  setLlmResponse(result);
  setSubmitting(false);
};





const handleOpen = async () => {
  setOpen(true);
  setLoading(true);

  // 1️⃣ Get backend context fields
  const backendFields = await fetchLLMContextFields();

  // 2️⃣ Always add local Redux-based context
  // ✅ Always add ALL Redux-based contexts
const fields = [
  ...backendFields,
  "My_Portfolio_Items",
  "Specific Portfolio Alert",
  "Specific Favorites Alert",
  "Specific Opportunity Alert"
];


  // 3️⃣ Fetch backend context values (skip local one!)
  const fieldValues = {};
  await Promise.all(
    backendFields.map(async (field) => {
      fieldValues[field] = await fetchContextField(field);
    })
  );
  fieldValuesRef.current = fieldValues;

  // 4️⃣ Availability: backend fields by fetched value, local always true
  const availability = {};
fields.forEach((field) => {
  if (
    field === "My_Portfolio_Items" ||
    field === "Specific Portfolio Alert" ||
    field === "Specific Favorites Alert" ||
    field === "Specific Opportunity Alert"
  ) {
    availability[field] = true; // always available
  } else {
    availability[field] = !!fieldValues[field];
  }
});

  setAvailableContexts(availability);

  // 5️⃣ Defaults
  const defaults = {};
  fields.forEach((field) => {
    defaults[field] = false;
  });
  setContextSelections(defaults);

  // 6️⃣ Save options for Contexts section
  setContextOptions(fields);

  // 7️⃣ Initial prompt + token
  const rawPrompt = buildPrompt(defaults);
  const assembled = `${systemPrompt}
${questionTypePrompts[questionType] || ""}
${investorProfile}
${rawPrompt}`;
  setAutoPrompt(assembled);

  // setPrompt(assembled);

  const expandedPrompt = finalizePrompt(assembled, fields, fieldValuesRef);
  setTokenCount(estimateTokenCount(expandedPrompt));

  setLoading(false);
};





  const handleClose = () => setOpen(false);

  // Expose showModal function to parent component via ref
  useImperativeHandle(ref, () => ({
    showModal: handleOpen
  }));


  const handleContextToggle = (item) => {
  const isSelecting = !contextSelections[item];
  setEditingContext(isSelecting ? item : '');

  setContextSelections(prev => {
  const updated = { ...prev, [item]: isSelecting };
  return updated;
});
};


  return (
  <>
    <NavButton
      icon={<PsychologyIcon />}
      label="LLM"
      onClick={handleOpen}
      primary={false}
      disabled={disabled}
      tooltip="Launch LLM-based investment assistant"
    />

    <Dialog
  open={open}
  onClose={handleClose}
  maxWidth={false} // disables MUI preset sizes
  fullWidth
  sx={{ 
    '& .MuiDialog-paper': { 
      width: '90%',  // take up 90% of screen width
      maxWidth: '1600px' // or larger if you want
    }
  }}
>
      <DialogTitle>
        <Typography variant="h6">Let's talk with some LLM</Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>


            {/* === LEFT: Preview Box === */}
<Grid item xs={5}>
  <ContextPreviewPane
  questionType={questionType}
  previewedContext={previewedContext}
  promptTemplates={promptTemplates}
  getPreviewContent={getPreviewContent}
  alerts={alerts}
  selectedSet={selectedSet}
  setSelectedSet={setSelectedSet}
  systemPrompt={systemPrompt}
  questionTypePrompts={questionTypePrompts}
  investorProfile={investorProfile}
  contextSelections={contextSelections}
  buildPrompt={buildPrompt}
  setAutoPrompt={setAutoPrompt}   // ✅ FIXED LINE
  saving={saving}
  loading={loading}
  submitting={submitting}
  strategy={strategy}
  // handleSave={handleSave}
/>


</Grid>



            {/* === CENTER: Context Buttons === */}
           <Grid item xs={2}>

{/* === QUESTION TYPE SELECTOR === */}
<QuestionTypeSelector
  questionType={questionType}
  setQuestionType={(value) => {
    setQuestionType(value);

if (value === 'Specific Portfolio Alert') {
  setPfAlerts(getPFAlertData());
}
if (value === 'Specific Favorites Alert') {
  setFavAlerts(getFavoritesAlertData());
}
if (value === 'Specific Opportunity Alert') {
  setOppAlerts(getOpportunitiesAlertData());
}
if (value === 'Specific stock - My Portfolio') {
  setPf(getPortfolioData());
}
if (value === 'My_Portfolio_Items') {
  setPf(getPortfolioData()); // reuse your selector
}




  }}
/>



<Typography
  variant="subtitle2"
  onClick={() => setShowContexts(!showContexts)}
  sx={{ mt: 2, cursor: 'pointer', fontWeight: 'bold' }}
>
  Contexts {showContexts ? "▼" : "▶"}
</Typography>

<Collapse in={showContexts}>
  <ButtonGroup
    size="small"
    orientation="vertical"
    fullWidth
    sx={{ gap: 1, mt: 1 }}
  >
    {contextOptions.map((option) => {
      const isSelected = contextSelections[option];
      return (
        <Button
          key={option}
          onClick={() => handleContextToggle(option)}
          endIcon={
            <CodeIcon
              fontSize="small"
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewContext(option);
              }}
            />
          }
          variant={isSelected ? "contained" : "outlined"}
          sx={{ justifyContent: 'space-between' }}
        >
          {option.toUpperCase()}
        </Button>
      );
    })}
  </ButtonGroup>
</Collapse>


{/* === TEMPLATES SELECTOR === */}
  <Typography
    variant="subtitle2"
    onClick={() => setShowTemplates(!showTemplates)}
    sx={{ mt: 2, cursor: 'pointer', fontWeight: 'bold' }}
  >
    Prompt Templates {showTemplates ? "▼" : "▶"}
  </Typography>

  <Collapse in={showTemplates}>
    <ButtonGroup
  size="small"
  orientation="vertical"
  fullWidth
  sx={{ gap: 1, mt: 1 }}
>
  {Object.entries(promptTemplates)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, template]) => {
      const isActive = activeTemplates.has(key);
      return (
        <Button
          key={key}
          onClick={() => {
            setActiveTemplates(prev => {
  const newSet = new Set(prev);
  if (newSet.has(key)) {
    newSet.delete(key);
  } else {
    newSet.add(key);
  }
  return newSet; // nothing else!
});



          }}
          startIcon={{
            quantitative: <CalculateIcon fontSize="small" />,
            riskFirst: <WarningIcon fontSize="small" />,
          }[key] || null}
          endIcon={
            <CodeIcon
              fontSize="small"
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewTemplate(key);
              }}
            />
          }
          variant={isActive ? "contained" : "outlined"}
          sx={{ justifyContent: 'space-between' }}
        >
          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
        </Button>
      );
    })}
</ButtonGroup>

  </Collapse>

  {/* ✅ LLM Selector at bottom */}
  <FormControl size="small" sx={{ mt: 3, width: '100%' }}>
    <InputLabel id="llm-model-label">LLM Model</InputLabel>
    <Select
      labelId="llm-model-label"
      id="llm-model-select"
      value={llmModel}
      label="LLM Model"
      onChange={(e) => setLlmModel(e.target.value)}
    >
      <MenuItem value="gemini">
        {llmModel === "gemini" ? "✅ " : ""}Gemini
      </MenuItem>
      <MenuItem value="deepseek-chat">
        {llmModel === "deepseek-chat" ? "✅ " : ""}DeepSeek Chat
      </MenuItem>
      <MenuItem value="deepseek-reasoner">
        {llmModel === "deepseek-reasoner" ? "✅ " : ""}DeepSeek Reasoner
      </MenuItem>
    </Select>
  </FormControl>

 {/* ✅ Search Stock */}



<Button
  fullWidth
  variant="contained"
  color="primary"
  sx={{ mt: 2 }}
  disabled={questionType !== "Specific stock - General"}
  onClick={() => setSearchDialogOpen(true)}
>
  Select Stock
</Button>


</Grid>


            {/* === RIGHT: Prompt === */}
            <Grid item xs={5}>
  <Typography variant="subtitle1">
  Prompt to {llmModel} LLM:
</Typography>


  

  <Box
    sx={{
      border: "1px solid #ccc",
      borderRadius: "4px",
      padding: "8px",
      height: "60vh",
      overflowX: "auto",
      overflowY: "auto",
      backgroundColor: "#fafafa",
      fontFamily: "monospace",
      fontSize: "0.8rem",
      whiteSpace: "pre",
    }}
  >
    <textarea
  value={autoPrompt + "\n\n" + userPrompt}
  onChange={(e) => {
    const autoLen = autoPrompt.length;
    const total = e.target.value;
    const newUserPart = total.slice(autoLen).trimStart();
    setUserPrompt(newUserPart);

    const expanded = finalizePrompt(autoPrompt + "\n\n" + newUserPart, contextOptions, fieldValuesRef);
    setTokenCount(estimateTokenCount(expanded));
  }}
  style={{
    width: "100%",
    height: "100%",
    border: "none",
    resize: "none",
    background: "transparent",
    outline: "none",
    fontFamily: "inherit",
    fontSize: "inherit",
    whiteSpace: "pre",
  }}
/>

  </Box>
  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
    Tokens: {tokenCount}
    <Typography
      variant="caption"
      color={tokenLimitWarning ? "error" : "text.secondary"}
      sx={{ display: 'block' }}
    >
      {tokenLimitWarning ? tokenLimitWarning : "Within limit"}
    </Typography>
  </Typography>
</Grid>

          </Grid>


        )}
      </DialogContent>

      <DialogActions>
  <Button onClick={handleClose} variant="outlined" disabled={saving || submitting}>
    Cancel
  </Button>
  <Button
  onClick={handleSubmitToLLM}
  variant="contained"
  color="secondary"
  disabled={
  saving ||
  loading ||
  submitting ||
  !!tokenLimitWarning ||
(
  !Object.values(contextSelections).some(Boolean) &&
  selectedPfAlerts.size === 0 &&
  selectedFavAlerts.size === 0 &&
  selectedOppAlerts.size === 0
)

}


>
  {submitting ? "Submitting..." : "Submit to LLM"}
</Button>

</DialogActions>


    </Dialog>

    {/* LLM Response Dialog */}
    <Dialog open={llmDialogOpen} onClose={() => setLlmDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
  {submitting ? `Submitting to ${llmModel}...` : `${llmModel} LLM Response`}
</DialogTitle>
      <DialogContent dividers>
        { submitting ? (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
    <Typography sx={{ mb: 2 }}>{`Submitting to ${llmModel}...`}</Typography>
    <CircularProgress size={60} />
  </Box>
) : (
  <TextField
    value={llmResponse}
    multiline
    fullWidth
    minRows={16}
    InputProps={{ readOnly: true }}
  />
)}

      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setLlmDialogOpen(false)}
          variant="outlined"
          disabled={submitting}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>

<Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Select a stock</DialogTitle>
  <DialogContent dividers>
    <SearchBoxOnly
      onSelectTicker={(val) => {
  const [ticker, ...rest] = val.split(' - ');
  const company = rest.join(' - ');
  setSelectedGeneralStock({ company, ticker });
  setSearchDialogOpen(false);
}}

    />
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setSearchDialogOpen(false)}>Close</Button>
  </DialogActions>
</Dialog>


  </>
);

});

export default LLM_analysis;
